"use client";

import {
  cloneElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import styles from "./DropdownMenu.module.css";

export default function DropdownMenu({
  trigger,
  items,
  align = "start",
  offset = 6,
  onOpenChange,
  matchTriggerWidth = false,
  header,
  emptyState,
  maxHeight,
}) {
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const [matchedWidth, setMatchedWidth] = useState(null);
  const [computedMaxHeight, setComputedMaxHeight] = useState(null);

  const setOpen = useCallback(
    (next) => {
      setIsOpen(next);
      onOpenChange?.(next);
    },
    [onOpenChange]
  );

  const handleTriggerClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(!isOpen);
    trigger.props.onClick?.(event);
  };

  const computePosition = useCallback(() => {
    const triggerEl = triggerRef.current;
    const menuEl = menuRef.current;
    if (!triggerEl || !menuEl) return;

    const triggerRect = triggerEl.getBoundingClientRect();
    if (matchTriggerWidth) {
      setMatchedWidth(triggerRect.width);
    }
    const menuRect = menuEl.getBoundingClientRect();
    const effectiveMenuWidth = matchTriggerWidth ? triggerRect.width : menuRect.width;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const margin = 8;

    let left =
      align === "end"
        ? triggerRect.right - effectiveMenuWidth
        : triggerRect.left;
    if (left + effectiveMenuWidth > viewportWidth - margin) {
      left = viewportWidth - margin - effectiveMenuWidth;
    }
    if (left < margin) {
      left = margin;
    }

    // Available space above and below the trigger (already accounting for
    // the offset gap and the safe viewport margin).
    const availableBelow = viewportHeight - triggerRect.bottom - offset - margin;
    const availableAbove = triggerRect.top - offset - margin;

    // Place below if there's room (or at least more room than above);
    // otherwise flip above. The menu's max-height is then capped to whatever
    // space we have on that side, so it never overlaps the trigger.
    const placeBelow =
      availableBelow >= menuRect.height || availableBelow >= availableAbove;
    let top;
    let nextMaxHeight;
    if (placeBelow) {
      top = triggerRect.bottom + offset;
      nextMaxHeight = Math.max(120, availableBelow);
    } else {
      nextMaxHeight = Math.max(120, availableAbove);
      const cappedHeight = Math.min(menuRect.height, nextMaxHeight);
      top = triggerRect.top - offset - cappedHeight;
    }

    // Honour an explicit cap from the consumer (Select uses this for
    // searchable menus to keep the list a manageable length).
    if (typeof maxHeight === "number") {
      nextMaxHeight = Math.min(nextMaxHeight, maxHeight);
    }

    setPosition({ left, top });
    setComputedMaxHeight(nextMaxHeight);
  }, [align, offset, matchTriggerWidth, maxHeight]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    computePosition();
  }, [isOpen, items, computePosition]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleOutsideMouseDown = (event) => {
      if (menuRef.current?.contains(event.target)) return;
      if (triggerRef.current?.contains(event.target)) return;
      setOpen(false);
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };
    const handleReposition = () => computePosition();

    window.addEventListener("mousedown", handleOutsideMouseDown, true);
    window.addEventListener("keydown", handleKeyDown, true);
    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      window.removeEventListener("mousedown", handleOutsideMouseDown, true);
      window.removeEventListener("keydown", handleKeyDown, true);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [isOpen, setOpen, computePosition]);

  const enhancedTrigger = cloneElement(trigger, {
    ref: triggerRef,
    onClick: handleTriggerClick,
    "aria-haspopup": "menu",
    "aria-expanded": isOpen || undefined,
  });

  const menuClasses = [
    styles.menu,
    align === "end" ? styles.menuAlignEnd : "",
    header ? styles.menuWithHeader : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <>
      {enhancedTrigger}
      {isOpen && typeof document !== "undefined"
        ? createPortal(
            <div
              ref={menuRef}
              className={menuClasses}
              role="menu"
              style={{
                left: position.left,
                top: position.top,
                width: matchTriggerWidth && matchedWidth ? `${matchedWidth}px` : undefined,
                minWidth: matchTriggerWidth && matchedWidth ? `${matchedWidth}px` : undefined,
                maxWidth: matchTriggerWidth && matchedWidth ? `${matchedWidth}px` : undefined,
                maxHeight: computedMaxHeight ? `${computedMaxHeight}px` : undefined,
              }}
            >
              {header ? <div className={styles.header}>{header}</div> : null}
              {items.length === 0 && emptyState ? (
                <div className={styles.emptyState}>{emptyState}</div>
              ) : null}
              <div className={styles.scrollArea}>
              <ul className={styles.menuList}>
                {items.map((item, index) => {
                  const key = item.id ?? `item-${index}`;
                  if (item.type === "divider") {
                    return (
                      <li
                        key={key}
                        className={styles.divider}
                        role="separator"
                        aria-hidden="true"
                      />
                    );
                  }
                  if (item.type === "section") {
                    return (
                      <li key={key} className={styles.sectionLabel} role="presentation">
                        {item.label}
                      </li>
                    );
                  }
                  const buttonClasses = [
                    styles.menuButton,
                    item.selected ? styles.selected : "",
                    item.danger ? styles.danger : "",
                  ]
                    .filter(Boolean)
                    .join(" ");
                  return (
                    <li key={key} className={styles.menuItem}>
                      <button
                        type="button"
                        className={buttonClasses}
                        role="menuitem"
                        disabled={item.disabled}
                        onClick={() => {
                          if (item.disabled) return;
                          setOpen(false);
                          item.onSelect?.();
                        }}
                      >
                        {item.icon ? (
                          <span className={styles.icon} aria-hidden="true">{item.icon}</span>
                        ) : null}
                        <span className={styles.label}>{item.label}</span>
                        {item.kbd ? <span className={styles.kbd}>{item.kbd}</span> : null}
                      </button>
                    </li>
                  );
                })}
              </ul>
              </div>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
